import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Sober DS (v2) — Table. Semantic <table> primitives mirroring the `.dtable`
 * showcase spec: collapsed borders, fs-14 body, fs-12 bold secondary head,
 * outline row dividers (last row borderless), app-bg row hover. Numeric cells:
 * pass `numeric` to TH/TD (or the `num` class) for right alignment.
 *
 * Wrap <Table> in an overflow-x-auto container when horizontal scroll matters.
 */
export const Table = React.forwardRef<HTMLTableElement, React.TableHTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <table
      ref={ref}
      className={cn('w-full border-collapse text-so-14 text-sober-text', className)}
      {...props}
    />
  )
);
Table.displayName = 'Table';

export const THead = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => <thead ref={ref} className={cn(className)} {...props} />);
THead.displayName = 'THead';

export const TBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn(
      '[&_tr:last-child>td]:border-0 [&_tr:hover]:bg-sober-app',
      className
    )}
    {...props}
  />
));
TBody.displayName = 'TBody';

export const TR = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr ref={ref} className={cn('transition-colors', className)} {...props} />
));
TR.displayName = 'TR';

export interface THProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  numeric?: boolean;
}

export const TH = React.forwardRef<HTMLTableCellElement, THProps>(
  ({ className, numeric, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        'px-4 py-3 text-so-12 font-bold text-left text-sober-secondary whitespace-nowrap border-b border-sober-outline',
        numeric && 'text-right',
        className
      )}
      {...props}
    />
  )
);
TH.displayName = 'TH';

export interface TDProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  numeric?: boolean;
}

export const TD = React.forwardRef<HTMLTableCellElement, TDProps>(
  ({ className, numeric, ...props }, ref) => (
    <td
      ref={ref}
      className={cn(
        'px-4 py-3 align-middle border-b border-sober-outline',
        numeric && 'text-right',
        className
      )}
      {...props}
    />
  )
);
TD.displayName = 'TD';
